import { useQuery } from 'react-query';

import { useInfiniteQuery } from 'core/hooks';
import {
  getNextReelsPage,
  getPartFinderCatalogFacets,
  getPartFinderCatalogFamilies,
  getPartFinderFamilyDefinition,
  getPartFinderReels,
} from 'partFinder/services/partFinderSearch';
import { IPartFinderCatalogFamily, IPartFinderReel, IPartFinderSelection } from 'partFinder/types';

// The stock narrowing is the server's to do; the manufacturer and category filters run on the rows.
export const useGetPartFinderCatalogFamilies = ({
  inStock,
  locationIds,
}: Pick<IPartFinderSelection, 'inStock' | 'locationIds'>) =>
  useInfiniteQuery<IPartFinderCatalogFamily>(
    getPartFinderCatalogFamilies,
    'partFinderCatalogFamilies',
    {
      filters: {
        ...(inStock ? { inStock: 1 } : {}),
        ...(locationIds.length ? { locationIds } : {}),
      },
    },
    { fetchAllPages: true }
  );

export const useGetPartFinderCatalogFacets = () =>
  useQuery('partFinderCatalogFacets', ({ signal }) => getPartFinderCatalogFacets(signal));

export const useGetPartFinderFamilyDefinition = (
  familyId: number,
  selection: IPartFinderSelection
) =>
  useQuery(
    ['partFinderFamilyDefinition', familyId, selection],
    ({ signal }) => getPartFinderFamilyDefinition(familyId, selection, signal),
    // A family that is not available answers 422 every time: no retries before the page says so.
    { enabled: Boolean(familyId), keepPreviousData: true, retry: false }
  );

// The family's rows, page after page until all are in, the parts the search picked first: those
// show as soon as their page is in, and a filter the user changes later finds every part.
export const useGetPartFinderReels = (familyId: number, selection: IPartFinderSelection) =>
  useInfiniteQuery<IPartFinderReel>(
    getPartFinderReels,
    'partFinderReels',
    { familyId, selection },
    // A page pulls live inventory for up to 500 parts: a failed one is not pulled again on its own;
    // the line under the family name says loading stopped. Each page starts where the last ended.
    { fetchAllPages: true, retry: false, getNextPageParam: getNextReelsPage }
  );
